import React, { useState, useEffect, useRef } from 'react';
import { IoWarningOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import { sendOtp, verifyOtp } from '../lib/authClient';
import { useAuth } from '../context/AuthContext';

interface InlineOtpVerificationProps {
  phone: string;
  onVerified: () => void;
  onCancel?: () => void;
  otpAlreadySent?: boolean;
}

const InlineOtpVerification: React.FC<InlineOtpVerificationProps> = ({ phone, onVerified, onCancel, otpAlreadySent = false }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(30);
  const [isSent, setIsSent] = useState(otpAlreadySent);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { login } = useAuth();

  useEffect(() => {
    let mounted = true;
    const focusFirstInput = () => window.setTimeout(() => otpInputRefs.current[0]?.focus(), 100);

    if (otpAlreadySent) {
      const focusTimer = focusFirstInput();
      return () => window.clearTimeout(focusTimer);
    }

    const triggerSendOtp = async () => {
      setIsLoading(true);
      setError('');
      const { success, error: apiError } = await sendOtp(phone);
      if (!mounted) return;

      setIsLoading(false);
      if (success) {
        setIsSent(true);
        setResendTimer(30);
        focusFirstInput();
      } else {
        setError(apiError || 'Failed to send OTP.');
      }
    };

    triggerSendOtp();
    return () => { mounted = false; };
  }, [otpAlreadySent, phone]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setIsLoading(true);
    setError('');
    const { success, error: apiError } = await sendOtp(phone);
    setIsLoading(false);
    if (success) {
      setResendTimer(30);
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } else {
      setError(apiError || 'Failed to resend OTP.');
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const cleanValue = value.replace(/[^\d]/g, '');
    if (!cleanValue && value !== '') return;

    const newOtp = [...otp];
    if (cleanValue.length > 1) {
      const pastedDigits = cleanValue.substring(0, 6).split('');
      for (let i = 0; i < pastedDigits.length; i++) {
        if (index + i < 6) {
          newOtp[index + i] = pastedDigits[i];
        }
      }
      setOtp(newOtp);
      const nextIndex = Math.min(index + pastedDigits.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
      return;
    }

    newOtp[index] = cleanValue;
    setOtp(newOtp);

    if (cleanValue && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const otpString = otp.join('');
    if (otpString.length < 4) return;

    setIsLoading(true);
    setError('');

    const { success, token, error: apiError } = await verifyOtp(phone, otpString);
    setIsLoading(false);

    if (success && token) {
      await login(phone, token);
      onVerified();
    } else {
      setError(apiError || 'Invalid OTP');
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-5 sm:p-6 animate-fade-in mt-4">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            Verify Mobile Number
            {isSent && <IoCheckmarkCircleOutline className="text-emerald-500" />}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            We sent a 6-digit code to <span className="font-bold text-gray-800">+91 {phone}</span>
          </p>
        </div>
        {onCancel && (
          <button 
            onClick={onCancel}
            className="text-primary text-sm font-semibold hover:underline"
          >
            Change
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 bg-red-50 text-red-600 p-3 rounded-lg text-sm font-medium border border-red-100 animate-fade-in">
          <IoWarningOutline className="text-lg flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}

      <div className="flex justify-between gap-2 sm:gap-3 mb-6">
        {otp.map((digit, idx) => (
          <input
            key={idx}
            ref={(el) => { otpInputRefs.current[idx] = el; }}
            type="tel"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpChange(idx, e.target.value)}
            onKeyDown={(e) => handleOtpKeyDown(idx, e)}
            disabled={isLoading || !isSent}
            className={`w-full aspect-square text-center text-xl sm:text-2xl font-bold rounded-xl border-2 transition-all text-gray-900 ${
              error ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200 bg-gray-50 text-gray-900 focus:border-primary focus:bg-white focus:text-gray-900 focus:ring-4 focus:ring-primary/10'
            }`}
          />
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <button
          onClick={handleVerify}
          disabled={isLoading || otp.join('').length < 4 || !isSent}
          className="ui-button-accent flex-1 h-[52px] text-base rounded-xl shadow-md hover:shadow-lg w-full"
        >
          {isLoading ? (
            <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            'Verify & Continue'
          )}
        </button>

        <button
          onClick={handleResend}
          disabled={resendTimer > 0 || isLoading}
          className={`text-sm font-semibold whitespace-nowrap transition-colors ${
            resendTimer > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-primary hover:text-orange-600 underline'
          }`}
        >
          {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
        </button>
      </div>
    </div>
  );
};

export default InlineOtpVerification;
